import { SoftTag } from '../../Tag/SoftTag';
import { ContainerOutlined, FileAddOutlined, RollbackOutlined } from '@ant-design/icons';
import {
  ApprovedEstimateConfirm,
  DashedButton,
  InventorySelect,
  ServiceExtraForm,
} from '@components';
import { Utils } from '@utils';
import { Button, Col, Drawer, Input, InputNumber, Row, Typography } from 'antd';
import React, { useState } from 'react';
import { IServiceDetailsPresentation, headerLabelStyle } from './ServiceDetailsCard.props';
import { RemoveServiceButton } from './RemoveServiceButton';

const { Text } = Typography;

export const ServiceDetailsMobile: React.FC<IServiceDetailsPresentation> = ({
  service,
  disabled,
  vat,
  showDiscount,
  showTotals,
  subTotal,
  vatAmount,
  parts,
  onAddPart,
  onAddPartFromInventory,
  onUploadParts,
  onReturnParts,
  onRemovePart,
  onRemove,
  canRemove,
  onUpdatePart,
  onAddPartToPurchaseOrder,
  handlePriceUpdate,
  handleDurationUpdate,
  handleDiscountUpdate,
  handleNoteUpdate,
}) => {
  const [inventoryOpen, setInventoryOpen] = useState(false);

  return (
    <>
      {/* Note input */}
      <div style={{ marginBottom: 8 }}>
        <Text style={headerLabelStyle}>Note</Text>
        <Input.TextArea
          placeholder="Note..."
          autoSize={{ minRows: 1 }}
          value={service.note || ''}
          disabled={disabled}
          onChange={(e) => handleNoteUpdate(service.id, e.target.value)}
          style={{ fontSize: 12 }}
        />
      </div>

      <Row gutter={[8, 10]}>
        <Col span={8}>
          <Text style={headerLabelStyle}>{service.isFixed ? 'Price (£)' : '£/hour'}</Text>
          <InputNumber
            min={0}
            step=".01"
            precision={2}
            style={{ width: '100%' }}
            value={service.price}
            disabled={disabled}
            onChange={(value: number | null) =>
              handlePriceUpdate(
                service.id,
                value !== null ? Number(Utils.formatNumber(value, 2)) : 0
              )
            }
          />
        </Col>
        <Col span={8}>
          <Text style={headerLabelStyle}>Duration</Text>
          <InputNumber
            min={0}
            step=".01"
            precision={2}
            style={{ width: '100%' }}
            value={service.duration}
            disabled={disabled}
            onChange={(value: number | null) =>
              handleDurationUpdate(
                service.id,
                value !== null ? Number(Utils.formatNumber(value, 2)) : 0
              )
            }
          />
        </Col>
        {showDiscount && (
          <Col span={8}>
            <Text style={headerLabelStyle}>Disc. (%)</Text>
            <InputNumber
              min={0}
              max={100}
              step="0.01"
              precision={2}
              style={{ width: '100%' }}
              value={service.discount}
              disabled={disabled}
              onChange={(value: number | null) =>
                handleDiscountUpdate(
                  service.id,
                  value !== null ? Number(Utils.formatNumber(value, 2)) : 0
                )
              }
            />
          </Col>
        )}
        {showTotals && (
          <Col span={24}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'flex-end',
                gap: 14,
                marginTop: 4,
                alignItems: 'center',
              }}
            >
                <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                  Sub Total: <strong style={{ color: '#555' }}>{Utils.formatCurrency(subTotal)}</strong>
                </span>
                <span style={{ color: '#ddd' }}>|</span>
                <span style={{ fontSize: 13, color: '#8c8c8c' }}>
                  VAT {Utils.formatVat(vat, service.isVat)}:{' '}
                  <strong style={{ color: '#555' }}>{Utils.formatCurrency(vatAmount)}</strong>
                </span>
                <span style={{ color: '#ddd' }}>|</span>
                <SoftTag
                  group="blue"
                  style={{ fontSize: 14, fontWeight: 700, padding: '2px 8px', margin: 0 }}
                >
                  {Utils.formatCurrency(Utils.roundToTwoDecimals(subTotal + vatAmount))}
                </SoftTag>
            </div>
          </Col>
        )}
      </Row>

      {/* Parts & extras */}
      <div style={{ marginTop: 16, borderTop: '1px solid #f0f0f0', paddingTop: 12 }}>
        {parts.map((servicePart, index) => (
          <ServiceExtraForm
            key={`${service.id}part${index}`}
            index={index}
            extra={servicePart}
            onRemove={() => onRemovePart(index)}
            onUpdate={(values) => onUpdatePart(index, values)}
            disabled={disabled}
            onAddToPurchaseOrder={onAddPartToPurchaseOrder ? () => onAddPartToPurchaseOrder(index) : undefined}
          />
        ))}
        {/* On an approved estimate job both ask first: the price was agreed. */}
        <div style={{ marginTop: 8 }}>
          <ApprovedEstimateConfirm onConfirm={onAddPart}>
            {(trigger) => <DashedButton title="Add extra" disabled={disabled} {...trigger} />}
          </ApprovedEstimateConfirm>
        </div>
        <ApprovedEstimateConfirm onConfirm={() => setInventoryOpen(true)}>
          {(trigger) => (
            <Button
              block
              icon={<ContainerOutlined />}
              disabled={disabled}
              style={{ marginTop: 8 }}
              {...trigger}
            >
              From Inventory
            </Button>
          )}
        </ApprovedEstimateConfirm>
        {onUploadParts && (
          <Button
            block
            icon={<FileAddOutlined />}
            disabled={disabled}
            onClick={onUploadParts}
            style={{ marginTop: 8 }}
          >
            Upload parts
          </Button>
        )}
        {onReturnParts && (
          <Button
            block
            icon={<RollbackOutlined />}
            disabled={disabled}
            onClick={onReturnParts}
            style={{ marginTop: 8 }}
          >
            Return parts
          </Button>
        )}
        {/* The desktop lookup is a 650px popover, which a phone has no room for. */}
        <Drawer
          title="Add from inventory"
          placement="bottom"
          height="60vh"
          open={inventoryOpen}
          onClose={() => setInventoryOpen(false)}
          destroyOnHidden
        >
          <InventorySelect
            onSelect={(item) => {
              onAddPartFromInventory(item);
              setInventoryOpen(false);
            }}
          />
        </Drawer>
      </div>

      {onRemove && (
        <div style={{ marginTop: 12 }}>
          <RemoveServiceButton
            labelled
            disabled={disabled || canRemove === false}
            onRemove={onRemove}
          />
        </div>
      )}
    </>
  );
};
